/**
 * Carries the page's weather view through a seam crossing's change of stage (ADR-147 note 15): the view is kept while the old
 * stage ends, so that end does not destroy it, and the new stage takes it and stands it on the new region. A view nobody took,
 * because the new stage failed to prepare, is destroyed and its readings cleared, as a stage's own end would.
 */
export class WeatherHandover<V extends { destroy(): void }> {
  private kept: V | null = null;

  /**
   * Runs a restage with the current view kept.
   *
   * @param current The view of the stage that ends, or null.
   * @param restage Ends the old stage and prepares the new one; may throw.
   * @param cleared Clears what a destroyed view published.
   * @throws Whatever `restage` throws, after the untaken view is destroyed.
   */
  public run(current: V | null, restage: () => void, cleared: () => void): void {
    this.kept = current;
    try {
      restage();
    } finally {
      const left = this.kept;
      this.kept = null;
      if (left !== null) {
        left.destroy();
        cleared();
      }
    }
  }

  /**
   * Whether a stage's end must leave a view alone, because a crossing carries it on.
   *
   * @param view The ending stage's view.
   * @returns True while that view is kept.
   */
  public keeps(view: V): boolean {
    return this.kept === view;
  }

  /**
   * Takes the kept view for the new stage.
   *
   * @returns The view, or null when no crossing carries one.
   */
  public take(): V | null {
    const view = this.kept;
    this.kept = null;
    return view;
  }
}
